import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';
import {
  ChevronLeft, Users, Copy, Check, Share2
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import { tenantSlugFromHost } from '../../utils/tenantDomains';
import referralNormalArt from '../../assets/referral-normal-20261009.png';
import referralMilestonesArt from '../../assets/referral-milestones-20261009.png';
import referralInviteArt from '../../assets/referral-invite-20261009.png';
import socialShareArt from '../../assets/referral-social-share-20261009.png';

const formatMoney = (value) => `$${Number(value || 0).toLocaleString(undefined, {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
})}`;

const formatFp = (value) => `${Number(value || 0).toLocaleString(undefined, {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
})} FP`;

const formatPercent = (value) => `${Number(value || 0).toLocaleString(undefined, {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
})}%`;

const ordinal = (value) => {
  const number = Number(value);
  const suffix = number % 100 >= 11 && number % 100 <= 13
    ? 'th' : ({ 1: 'st', 2: 'nd', 3: 'rd' }[number % 10] || 'th');
  return `${number}${suffix}`;
};

function getTenantHostSlug() {
  return tenantSlugFromHost();
}

export default function ReferralPage() {
  const navigate = useNavigate();
  const { tenantSlug } = useParams();
  const { user } = useAuth();
  const hostTenantSlug = getTenantHostSlug();
  const tenantPathPrefix = hostTenantSlug ? '' : (tenantSlug ? `/${tenantSlug}` : '');
  const requestScope = `${hostTenantSlug || tenantSlug || 'main'}:${user?.id || user?.username || 'guest'}`;
  const explorePath = `${tenantPathPrefix}/app/explore`;
  const [copied, setCopied] = useState(null); // 'code' | 'link'
  const [brandResult, setBrandResult] = useState({ scope: '', name: 'our games' });
  const brandName = brandResult.scope === requestScope ? brandResult.name : 'our games';
  const [rulesResult, setRulesResult] = useState({ scope: '', status: 'loading', data: null });
  const [statsResult, setStatsResult] = useState({ scope: '', status: 'loading', data: null });
  const rulesStatus = rulesResult.scope === requestScope ? rulesResult.status : 'loading';
  const statsStatus = statsResult.scope === requestScope ? statsResult.status : 'loading';
  const rules = rulesStatus === 'ready' ? rulesResult.data : {};
  const stats = statsStatus === 'ready' ? statsResult.data : {};
  // Never synthesize a shareable code: only the server-issued code can recruit.
  const code = String(stats.referral_code || user?.referral_code || '').trim().toUpperCase();
  const encodedCode = encodeURIComponent(code);
  const link = code ? `${window.location.origin}${tenantPathPrefix}/auth?mode=register&ref=${encodedCode}&preview=cosmic2` : '';

  useEffect(() => {
    let mounted = true;
    api.get('/api/referrals/stats')
      .then(res => {
        if (mounted) setStatsResult({ scope: requestScope, status: 'ready', data: res?.data || {} });
      })
      .catch(() => {
        if (mounted) setStatsResult({ scope: requestScope, status: 'unavailable', data: null });
      });
    api.get('/api/referrals/rules', {
      params: { _refresh: Date.now() },
      headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
    })
      .then(res => {
        if (mounted) setRulesResult({ scope: requestScope, status: 'ready', data: res?.data || {} });
      })
      .catch(() => {
        if (mounted) setRulesResult({ scope: requestScope, status: 'unavailable', data: null });
      });
    api.get('/api/branding')
      .then(res => {
        if (mounted) setBrandResult({ scope: requestScope, name: res?.data?.brand_name || 'our games' });
      })
      .catch(() => { /* keep fallback */ });
    return () => { mounted = false; };
  }, [requestScope]);

  const copy = async (text, kind) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(kind);
      setTimeout(() => setCopied(null), 1800);
    } catch (e) {
      console.warn(e);
    }
  };

  const totalReferrals = stats.total_referrals ?? stats.referral_count ?? 0;
  const cashEarned = stats.cash_earned ?? stats.total_cash_earned ?? stats.total_earned ?? 0;
  const freeplayEarned = stats.freeplay_earned ?? stats.fp_earned ?? stats.total_freeplay_earned ?? 0;
  const loadPercent = Number(rules.player_referrer_load_percent || 0);
  const referrerFp = Number(rules.player_referrer_bonus_sc || 0);
  const referredFp = Number(rules.player_referred_bonus_sc || 0);
  const sameSignupFp = Number(referrerFp) === Number(referredFp);
  const rewardMode = rules.player_referral_enabled === false ? 'off' : (
    rules.player_deposit_reward_mode || (Number(rules.player_referrer_load_percent || 0) > 0 ? 'normal' : 'off')
  );
  const milestones = rewardMode === 'milestone'
    ? (Array.isArray(rules.player_deposit_milestone_rewards) ? rules.player_deposit_milestone_rewards : [])
      .filter((row) => row?.enabled !== false && Number(row?.deposit_number) > 0 && Number(row?.percent) > 0)
      .sort((a, b) => Number(a.deposit_number) - Number(b.deposit_number))
    : [];
  const milestonePercentTotal = milestones.reduce((total, row) => total + Number(row.percent), 0);
  const showNormalReward = rulesStatus === 'ready' && rewardMode === 'normal' && loadPercent > 0;
  const showMilestoneReward = rulesStatus === 'ready' && rewardMode === 'milestone' && milestones.length > 0;
  const showSignupFp = rulesStatus === 'ready' && rules.player_referral_enabled !== false && (referrerFp > 0 || referredFp > 0);
  const heroArtMode = rulesStatus !== 'ready' ? 'pending' : showNormalReward ? 'normal' : showMilestoneReward ? 'milestone' : 'invite';
  const heroArt = showNormalReward ? referralNormalArt : showMilestoneReward ? referralMilestonesArt : referralInviteArt;

  return (
    <div
      className="player-app min-h-screen"
      style={{ color: '#FFFFFF', paddingBottom: 'calc(48px + env(safe-area-inset-bottom, 0px))' }}
      data-testid="explore-sub-referral"
    >
      {/* Header */}
      <div
        className="fixed inset-x-0 top-0 z-50"
        style={{
          zIndex: 50,
          background: 'rgba(6,23,14,0.96)',
          backdropFilter: 'blur(18px) saturate(160%)',
          WebkitBackdropFilter: 'blur(18px) saturate(160%)',
          borderBottom: '1px solid rgba(139,92,246,0.45)',
        }}
        data-testid="referral-header"
      >
        <div
          className="mx-auto flex max-w-3xl items-center gap-3 px-4 sm:px-6"
          style={{
            minHeight: 'calc(86px + env(safe-area-inset-top, 0px))',
            paddingTop: 'max(12px, env(safe-area-inset-top, 0px))',
            paddingBottom: '12px',
          }}
        >
          <button
            onClick={() => navigate(explorePath)}
            className="w-11 h-11 shrink-0 rounded-full flex items-center justify-center transition active:scale-95"
            style={{
              background: 'rgba(255,255,255,0.08)',
              border: '1px solid rgba(255,255,255,0.15)',
              color: '#FFFFFF',
            }}
            data-testid="referral-back"
            aria-label="Back"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="min-w-0 flex-1">
            <h1
              className="display font-extrabold text-xl sm:text-2xl flex items-center gap-2"
              style={{ color: '#FFFFFF' }}
            >
              <span
                className="w-9 h-9 rounded-xl flex items-center justify-center referral-bell-anim"
                style={{
                  background: 'linear-gradient(135deg, #8B5CF6, #6D28D9)',
                  boxShadow: '0 6px 16px rgba(139,92,246,0.55), inset 0 1px 0 rgba(255,255,255,0.45)',
                }}
                aria-hidden
              >
                <Users className="w-5 h-5 text-white" strokeWidth={2.6} />
              </span>
              Referral &amp; Earn
            </h1>
            <p className="text-xs leading-snug" style={{ color: 'rgba(255,255,255,0.7)' }}>Your invitation and earned rewards in one place</p>
          </div>
        </div>
      </div>

      <div
        className="max-w-3xl mx-auto px-4 sm:px-6 pb-8"
        style={{ paddingTop: 'calc(112px + env(safe-area-inset-top, 0px))' }}
      >
        {/* Hero / How it works card */}
        <div
          className="rounded-3xl p-6 sm:p-8 mb-6 relative overflow-hidden"
          style={{
            background:
              'linear-gradient(135deg, rgba(139,92,246,0.18) 0%, rgba(236,72,153,0.10) 100%)',
            border: '1px solid rgba(139,92,246,0.45)',
            boxShadow: '0 8px 22px rgba(139,92,246,0.18), inset 0 1px 0 rgba(255,255,255,0.05)',
          }}
        >
          <div className="mb-6 flex flex-col items-center gap-2 sm:flex-row sm:items-center sm:gap-6">
            {rulesStatus === 'ready' ? <img
              src={heroArt}
              alt=""
              data-testid="referral-hero-art"
              data-reward-art-mode={heroArtMode}
              className="h-36 w-44 shrink-0 object-contain sm:h-40 sm:w-44"
              loading="eager"
            /> : <div className="h-36 w-44 shrink-0 rounded-2xl bg-white/[0.04] sm:h-40" aria-hidden="true" data-testid="referral-art-pending" />}
            <div className="min-w-0 flex-1 text-center sm:text-left">
              <h2 className="display text-balance text-2xl font-extrabold leading-tight text-white sm:text-3xl">
                {showNormalReward && <>Earn <span className="text-amber-300">{formatPercent(loadPercent)}</span> on a friend&apos;s first deposit</>}
                {showMilestoneReward && <>Earn up to <span className="text-amber-300">{formatPercent(milestonePercentTotal)}</span> across their deposits</>}
                {rulesStatus === 'ready' && !showNormalReward && !showMilestoneReward && 'Invite friends to play together'}
                {rulesStatus === 'loading' && 'Checking referral offer'}
                {rulesStatus === 'unavailable' && 'Referral offer unavailable'}
              </h2>
              <p className="mt-2 text-sm leading-relaxed text-violet-100/85">
                {showNormalReward && `Based on their first successful deposit and paid to your ${rules.player_deposit_reward_currency === 'fp' ? 'Freeplay balance' : 'Main Wallet'}.`}
                {showMilestoneReward && `Each rate applies only to its own eligible deposit, across ${milestones.length} milestone${milestones.length === 1 ? '' : 's'}.`}
                {rulesStatus === 'loading' && 'Checking the current referral offer…'}
                {rulesStatus === 'unavailable' && 'Reward details could not load. Refresh to check the current offer.'}
                {rulesStatus === 'ready' && !showNormalReward && !showMilestoneReward && 'Your invite link is ready. There is no deposit reward offer active right now.'}
              </p>
              {showSignupFp && <p className="mt-2 text-sm leading-relaxed text-white/85">
                {sameSignupFp
                  ? <>After your friend verifies their phone, you each receive <strong className="text-amber-300">{formatFp(referrerFp)}</strong>.</>
                  : referrerFp > 0 && referredFp > 0
                    ? <>After your friend verifies their phone, you receive <strong className="text-amber-300">{formatFp(referrerFp)}</strong> and they receive <strong className="text-amber-300">{formatFp(referredFp)}</strong>.</>
                    : referrerFp > 0
                      ? <>After your friend verifies their phone, you receive <strong className="text-amber-300">{formatFp(referrerFp)}</strong>.</>
                      : <>After your friend verifies their phone, they receive <strong className="text-amber-300">{formatFp(referredFp)}</strong>.</>}
              </p>}
            </div>
          </div>

          {/* Code + Link: wait for the canonical code before enabling sharing. */}
          {code ? <><div className="space-y-3">
            <div>
              <p className="text-[10px] uppercase tracking-wider font-bold mb-1.5" style={{ color: '#C4B5FD' }}>
                Your Referral Code
              </p>
              <div
                className="flex min-w-0 items-stretch overflow-hidden rounded-[22px]"
                style={{ background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(255,255,255,0.08)' }}
              >
                <button
                  type="button"
                  onClick={() => copy(code, 'code')}
                  className="display min-w-0 flex-1 truncate px-3 py-3 text-left text-base font-extrabold tracking-wider transition active:scale-[0.99] mono min-[360px]:px-4 min-[360px]:text-xl"
                  style={{ color: '#FCD34D' }}
                  data-testid="ref-code"
                  title={code}
                >
                  {code}
                </button>
                <button
                  onClick={() => copy(code, 'code')}
                  className="display flex shrink-0 items-center gap-1.5 px-3 text-sm font-bold transition active:scale-95 min-[360px]:px-4"
                  style={{
                    background: 'linear-gradient(180deg, #8B5CF6, #6D28D9)',
                    color: '#fff',
                  }}
                  data-testid="ref-code-copy"
                >
                  {copied === 'code' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  {copied === 'code' ? 'Copied' : 'Copy'}
                </button>
              </div>
            </div>

            <div>
              <p className="text-[10px] uppercase tracking-wider font-bold mb-1.5" style={{ color: '#C4B5FD' }}>
                Your Invite Link
              </p>
              <div
                className="flex min-w-0 items-stretch overflow-hidden rounded-[22px]"
                style={{ background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(255,255,255,0.08)' }}
              >
                <button
                  type="button"
                  onClick={() => copy(link, 'link')}
                  className="min-w-0 flex-1 truncate px-3 py-3 text-left text-sm font-medium transition active:scale-[0.99] min-[360px]:px-4"
                  style={{ color: '#E9D5FF' }}
                  data-testid="ref-link"
                >
                  {link}
                </button>
                <button
                  onClick={() => copy(link, 'link')}
                  className="display flex shrink-0 items-center gap-1.5 px-3 text-sm font-bold transition active:scale-95 min-[360px]:px-4"
                  style={{
                    background: 'linear-gradient(180deg, #8B5CF6, #6D28D9)',
                    color: '#fff',
                  }}
                  data-testid="ref-link-copy"
                >
                  {copied === 'link' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  {copied === 'link' ? 'Copied' : 'Copy'}
                </button>
              </div>
            </div>
          </div>

          {/* Quick share row */}
          <div className="mt-5 grid grid-cols-2 gap-2.5 sm:grid-cols-4" aria-label="Share your invite link">
            <ShareBtn
              href={`https://wa.me/?text=${encodeURIComponent(`Join me on ${brandName}! ` + link)}`}
              label="WhatsApp"
              color="#25D366"
              spritePosition="0% 0%"
            />
            <ShareBtn
              href={`https://t.me/share/url?url=${encodeURIComponent(link)}&text=${encodeURIComponent(`Join me on ${brandName}!`)}`}
              label="Telegram"
              color="#229ED9"
              spritePosition="100% 0%"
            />
            <ShareBtn
              href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(link)}`}
              label="Facebook"
              color="#1877F2"
              spritePosition="0% 100%"
            />
            <ShareBtn
              href={`mailto:?subject=${encodeURIComponent(`Join ${brandName}`)}&body=${encodeURIComponent('Sign up with my code ' + code + ' — ' + link)}`}
              label="Email"
              color="#EF4444"
              spritePosition="100% 100%"
            />
            <button
              onClick={async () => {
                if (navigator.share) {
                  try { await navigator.share({ title: brandName, text: 'Join me!', url: link }); } catch {}
                } else {
                  copy(link, 'link');
                }
              }}
              className="display col-span-2 flex min-h-12 items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-bold transition active:scale-95 sm:col-span-4"
              style={{
                background: 'rgba(255,255,255,0.08)',
                border: '1px solid rgba(255,255,255,0.15)',
                color: '#fff',
              }}
              data-testid="ref-share-native"
            >
              <Share2 className="w-4 h-4" /> Share
            </button>
          </div></> : <div
            className="rounded-2xl border border-white/10 bg-black/20 px-4 py-5 text-center text-sm text-violet-100/80"
            role="status"
            data-testid="referral-code-pending"
          >
            {statsStatus === 'loading' ? 'Checking your referral code…' : 'Your referral code is unavailable. Refresh to try again.'}
          </div>}
        </div>

        <h2 className="display mb-3 text-lg font-bold text-white">Total referral earned</h2>
        {statsStatus === 'ready' ? <div className="mb-6 grid grid-cols-1 gap-2 min-[360px]:grid-cols-2 min-[360px]:gap-3 sm:grid-cols-3">
          <Stat label="Total Referrals" value={Number(totalReferrals || 0).toLocaleString()} accent="#8B5CF6" />
          <Stat label="Cash Earned" value={formatMoney(cashEarned)} accent="#22C55E" />
          <Stat className="min-[360px]:col-span-2 sm:col-span-1" label="Freeplay Earned" value={formatFp(freeplayEarned)} accent="#F59E0B" />
        </div> : <p className="mb-6 text-sm text-violet-100/70" role="status">
          {statsStatus === 'loading' ? 'Checking earned rewards…' : 'Earned rewards could not load right now.'}
        </p>}
        {showMilestoneReward && (
          <section className="rounded-2xl bg-[#101C2D] p-5 text-white sm:p-6" aria-label="Deposit milestone rewards">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <h2 className="display text-xl font-bold">Your milestone rewards</h2>
              <span className="text-sm font-semibold text-amber-300">Up to {formatPercent(milestonePercentTotal)} across {milestones.length} deposits</span>
            </div>
            <p className="mt-2 text-sm text-slate-300">These are possible rewards, not your earned balance. Each percentage applies to that deposit only.</p>
            <ol className="mt-5 divide-y divide-white/10">
              {milestones.map((row) => (
                <li key={row.deposit_number} className="flex flex-wrap items-center justify-between gap-3 py-3 first:pt-0 last:pb-0">
                  <div className="min-w-0">
                    <p className="font-semibold">{ordinal(row.deposit_number)} deposit</p>
                    <p className="text-xs text-slate-300">{Number(row.min_deposit_usd) > 0 ? `Minimum ${formatMoney(row.min_deposit_usd)}` : 'No minimum deposit'} · Paid to {row.reward_currency === 'fp' ? 'Freeplay' : 'Main Wallet'}</p>
                  </div>
                  <strong className="text-lg tabular-nums text-amber-300">Earn {formatPercent(row.percent)}</strong>
                </li>
              ))}
            </ol>
          </section>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, accent, className = '' }) {
  return (
    <div
      className={`min-w-0 rounded-[26px] p-3 text-center sm:p-4 ${className}`}
      style={{
        background: 'rgba(255,255,255,0.04)',
        border: `1px solid ${accent}55`,
      }}
    >
      <p className="text-[10px] uppercase tracking-wider font-bold" style={{ color: accent }}>
        {label}
      </p>
      <p
        className="display mono mt-0.5 min-w-0 break-words text-xl font-extrabold leading-tight min-[360px]:text-lg sm:text-2xl"
        style={{ color: '#FFFFFF' }}
        title={String(value)}
      >
        {value}
      </p>
    </div>
  );
}

function ShareBtn({ href, label, color, spritePosition }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="display flex min-h-12 min-w-0 items-center justify-center gap-2 rounded-xl px-2.5 py-2 text-sm font-bold transition active:scale-95 hover:brightness-110"
      style={{
        background: `${color}22`,
        border: `1px solid ${color}55`,
        color,
      }}
      data-testid={`ref-share-${label.toLowerCase()}`}
    >
      <span
        className="h-8 w-8 shrink-0"
        style={{
          backgroundImage: `url(${socialShareArt})`,
          backgroundPosition: spritePosition,
          backgroundSize: '200% 200%',
          backgroundRepeat: 'no-repeat',
        }}
        aria-hidden="true"
      />
      <span className="min-w-0 truncate">{label}</span>
    </a>
  );
}
